import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { useFamilyStore } from '../../stores/familyStore';
import { PrimaryParentOnly } from '../security/PermissionGate';
import { useEntitlements } from '../../hooks/useEntitlements';
import type { Database } from '../../lib/supabase';

type Child = Database['public']['Tables']['children']['Row'];

/**
 * Komponent wyświetlający listę dzieci z możliwością zarządzania
 */
export const ChildrenList: React.FC = () => {
  const { family, children, fetchChildren, addChild, isLoading, error } = useFamilyStore();
  const { plan: entitlements } = useEntitlements(family?.id);
  const [showAddForm, setShowAddForm] = useState(false);
  const [childName, setChildName] = useState('');
  const navigate = useNavigate();
  

  useEffect(() => {
    if (family) {
      fetchChildren(family.id);
    }
  }, [family, fetchChildren]);

  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!family || !childName) return;

    if (children.length >= entitlements.maxChildren) {
      alert(`Limit dzieci w Twoim planie został osiągnięty (maks. ${entitlements.maxChildren}). Zmień plan, aby dodać kolejne dziecko.`);
      return;
    }

    const newChildId = await addChild(childName);
    setChildName('');
    setShowAddForm(false);

    // Po dodaniu dziecka z ustawień ogólnych – jednorazowo przenieś do kalendarza tego dziecka
    if (newChildId) {
      navigate('/calendar', {
        state: { focusChildId: newChildId }
      });
    }
  };

  if (!family) {
    return <div className="text-center py-4">Brak aktywnej rodziny</div>;
  }

  if (isLoading) {
    return <div className="text-center py-4">Ładowanie dzieci...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Dzieci / Podopieczni</h2>
        <PrimaryParentOnly>
          <button 
            onClick={() => {
              if (!showAddForm && children.length >= entitlements.maxChildren) {
                alert(`W planie możesz mieć maksymalnie ${entitlements.maxChildren} ${entitlements.maxChildren === 1 ? 'dziecko' : 'dzieci'}. Przejdź do planu wyższego, aby dodać więcej.`);
                return;
              }
              setShowAddForm(!showAddForm);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
          >
            <span className="mr-1">{showAddForm ? '−' : '+'}</span> 
            {showAddForm ? 'Anuluj' : 'Dodaj'}
          </button>
        </PrimaryParentOnly>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}
      
      {showAddForm && (
        <form onSubmit={handleAddChild} className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="childName" className="block text-sm font-medium text-blue-700 mb-1">
                Imię dziecka
              </label>
              <input
                id="childName"
                type="text"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={isLoading || !childName}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors flex items-center"
            >
              {isLoading ? (
                <>
                  <span className="mr-2">Dodawanie...</span>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </>
              ) : (
                'Dodaj'
              )}
            </button>
          </div>
        </form>
      )}
      
      {children.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Brak dzieci \ podopiecznych w rodzinie. {!showAddForm && "Kliknij \"Dodaj\", aby dodać pierwsze dziecko \ podopiecznego."}
        </div>
      ) : (
        <div className="space-y-4">
          {children.map((child) => (
            <ChildItem key={child.id} child={child} />
          ))}
          {children.length >= entitlements.maxChildren && (
            <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
              Osiągnięto limit dzieci w planie. <a href="/pricing" className="text-blue-600 underline">Zobacz plany</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Komponent pojedynczego dziecka na liście
 */
const ChildItem: React.FC<{ child: Child }> = ({ child }) => {
  const { updateChild, removeChild, fetchChildren } = useFamilyStore();
  const { family } = useFamilyStore();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(child.name);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const handleSave = async () => {
    setIsLoading(true);
    
    // Przygotuj dane do aktualizacji
    const updateData: { name?: string } = {};
    
    // Sprawdź czy imię się zmieniło
    if (name.trim() !== child.name) {
      updateData.name = name.trim();
    }
    
    logger.debug('🔄 Dane do zapisu:', updateData);
    
    const success = await updateChild(child.id, updateData);
    
    if (success) {
      setIsEditing(false);
    }
    setIsLoading(false);
  };
  
  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    const success = await removeChild(child.id);
    if (success && family) {
      await fetchChildren(family.id);
    }
    setIsLoading(false);
    setShowDeleteConfirm(false);
  };
  
  return (
    <>
    <div className="border rounded-lg p-4 hover:bg-gray-50">
      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Imię dziecka
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <button 
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              Anuluj
            </button>
            <button 
              onClick={handleSave}
              disabled={isLoading}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm flex items-center"
            >
              {isLoading ? (
                <>
                  <span className="mr-1">Zapisywanie...</span>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </>
              ) : 'Zapisz'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-medium">{child.name}</h3>
          </div>
          <PrimaryParentOnly>
            <div className="flex space-x-2">
              <button 
                onClick={() => setIsEditing(true)}
                className="text-blue-600 hover:text-blue-800"
              >
                Edytuj
              </button>
              <button 
                onClick={handleDelete}
                className="text-red-600 hover:text-red-800"
              >
                Skasuj
              </button>
            </div>
          </PrimaryParentOnly>
        </div>
      )}
    </div>
    {showDeleteConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md border border-red-200">
          <h2 className="text-xl font-semibold mb-2 text-red-700">Skasować podopiecznego?</h2>
          <p className="text-sm text-red-600 mb-4">
            Tej operacji nie można cofnąć. Wszystkie dane powiązane z tym podopiecznym zostaną bezpowrotnie utracone.
          </p>
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-6 text-sm">
            Ostrzeżenie: usuwasz „{child.name}”.
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50"
              disabled={isLoading}
            >
              Anuluj
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 rounded text-sm bg-red-600 text-white hover:bg-red-700 disabled:bg-red-400"
              disabled={isLoading}
            >
              {isLoading ? 'Kasowanie...' : 'Skasuj'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};